import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, CameraOff, CheckCircle2, PartyPopper, ScanLine, Ticket, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageTitle, useApi, Skeleton, inputCls, btnPrimary } from "@/components/portal/ui";
import { QrScanner } from "@/components/portal/QrScanner";
import { CheckInFlipCard } from "@/components/portal/CheckInFlipCard";

export default function UsherScan() {
  const { data, reload } = useApi("/portal/usher/assignment");
  const [mode, setMode] = useState("jemaat");
  const [scheduleId, setScheduleId] = useState("");
  const [value, setValue] = useState("");
  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState(null);
  const [flip, setFlip] = useState(null);

  useEffect(() => {
    if (data?.schedules?.length && !scheduleId) setScheduleId(data.schedules[0].id);
  }, [data, scheduleId]);

  const submit = async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    if (mode === "jemaat") setFlip({ stage: "checking" });
    try {
      if (mode === "jemaat") {
        const { data: r } = await api.post("/portal/usher/checkin", { nig: code.replace(/^GIM:/, ""), schedule_id: scheduleId });
        setLast({ kind: "jemaat", title: r.name, sub: `${r.schedule_title} · NIG ${r.nig}` });
        setFlip({ stage: "done", icon: PartyPopper, title: "Selamat Beribadah!", sub: r.name, tone: "emerald" });
        toast.success(`${r.name} hadir`);
      } else if (mode === "tiket") {
        const { data: r } = await api.post("/portal/usher/scan-ticket", { code });
        setLast({ kind: "tiket", title: r.name, sub: `${r.event_title}${r.ticket_type ? ` · ${r.ticket_type}` : ""}` });
        toast.success(`Tiket valid · ${r.name}`);
      } else {
        const { data: r } = await api.post("/portal/usher/scan-checkpoint", { code });
        setLast({ kind: "checkpoint", title: r.name, sub: `${r.event_title}${r.ticket_type ? ` · ${r.ticket_type}` : ""}` });
        toast.success(`Checkpoint tervalidasi · ${r.name}`);
      }
      setValue("");
      reload();
    } catch (e) {
      setFlip(null);
      toast.error(errMsg(e));
    }
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const onScan = useCallback((v) => submitRef.current(v), []);

  if (!data) return <div className="p-5 pt-14"><Skeleton className="h-80" /></div>;
  if (!data.is_usher) {
    return (
      <div className="px-5 pt-16 text-center" data-testid="usher-not-assigned">
        <span className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 grid place-items-center"><ScanLine className="w-7 h-7" /></span>
        <p className="mt-4 font-extrabold text-slate-900">Belum ada tugas usher</p>
        <p className="mt-1 text-sm text-slate-500">Scanner hanya aktif bagi jemaat yang ditugaskan sebagai usher/penyambut atau panitia event.</p>
        <Link to="/portal/ministry" className="inline-block mt-5 text-sm font-bold text-blue-600">← My Ministry</Link>
      </div>
    );
  }

  return (
    <div data-testid="portal-usher-scan">
      <div className="px-5 pt-10 flex items-center gap-3">
        <Link to="/portal/ministry" data-testid="usher-back" className="w-10 h-10 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ArrowLeft className="w-5 h-5" /></Link>
        <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">{data.ministry_name} · {data.role}</p>
      </div>
      <PageTitle eyebrow="Petugas Usher" title="Scanner Absensi" />

      <div className="px-5 mt-2 grid grid-cols-3 gap-2">
        {[["jemaat", "Absensi", Users], ["tiket", "E-Tiket", Ticket], ["checkpoint", "Checkpoint", ScanLine]].map(([v, label, Icon]) => (
          <button key={v} onClick={() => { setMode(v); setLast(null); }} data-testid={`usher-mode-${v}`}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold transition active:scale-95 ${mode === v ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      <div className="px-5 mt-4 space-y-3">
        {mode === "jemaat" && (
          <select className={inputCls} value={scheduleId} onChange={(e) => setScheduleId(e.target.value)} data-testid="usher-schedule-select">
            {data.schedules.map((s) => <option key={s.id} value={s.id}>{`${s.title} · ${s.day} ${s.time}`}</option>)}
          </select>
        )}
        <form onSubmit={(e) => { e.preventDefault(); submit(value); }} className="flex gap-2">
          <input className={inputCls} value={value} onChange={(e) => setValue(e.target.value)} data-testid="usher-code-input"
            placeholder={mode === "jemaat" ? "NIG jemaat (mis. 26091001)" : mode === "tiket" ? "Kode E-Tiket (TIX-…)" : "Kode checkpoint (5 karakter)"} />
          <button className={btnPrimary} data-testid="usher-submit-button"><ScanLine className="w-4 h-4" /></button>
        </form>
        <button type="button" onClick={() => setCamera(!camera)} data-testid="usher-camera-toggle" className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 bg-white rounded-xl py-3 font-bold text-slate-700 active:scale-95 transition">
          {camera ? <><CameraOff className="w-4 h-4" />Matikan Kamera</> : <><Camera className="w-4 h-4" />Scan QR dengan Kamera</>}
        </button>
        {camera && <QrScanner onScan={onScan} testId="usher-qr-reader" />}

        {last && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex items-center gap-3" data-testid="usher-last-result">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
            <div className="min-w-0"><p className="font-bold text-emerald-900 truncate">{last.title}</p><p className="text-xs text-emerald-700 truncate">{last.sub}</p></div>
          </div>
        )}

        {mode === "jemaat" && (
          <div className="bg-white rounded-3xl p-5 border border-slate-100" data-testid="usher-today-list">
            <div className="flex items-center justify-between"><p className="font-extrabold text-slate-900 text-sm">Hadir Hari Ini</p><span className="text-[11px] font-bold bg-blue-100 text-blue-700 rounded-full px-3 py-1" data-testid="usher-today-count">{data.checkin_count} jemaat</span></div>
            <div className="mt-3 divide-y divide-slate-50 max-h-72 overflow-y-auto">
              {data.checkins.map((c) => (
                <div key={c.id} className="py-2.5 flex items-center justify-between gap-2">
                  <span className="min-w-0"><span className="block text-sm font-bold text-slate-900 truncate">{c.name}</span><span className="block text-[11px] text-slate-400">{c.schedule_title}</span></span>
                  <span className="shrink-0 text-[11px] font-bold text-slate-500 tabular-nums">{new Date(c.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
              {data.checkins.length === 0 && <p className="py-5 text-sm text-slate-500">Belum ada jemaat yang absen.</p>}
            </div>
          </div>
        )}

        {mode === "tiket" && (
          <div className="bg-white rounded-3xl p-5 border border-slate-100" data-testid="usher-event-list">
            <p className="font-extrabold text-slate-900 text-sm">Event Bertiket</p>
            <div className="mt-3 divide-y divide-slate-50">
              {data.events.map((e) => (
                <div key={e.id} className="py-2.5"><p className="text-sm font-bold text-slate-900">{e.title}</p><p className="text-[11px] text-slate-400">{e.start_date} · {e.ticket_types.length > 0 ? e.ticket_types.map((t) => t.name).join(" · ") : "Tiket tunggal"}</p></div>
              ))}
              {data.events.length === 0 && <p className="py-5 text-sm text-slate-500">Belum ada event bertiket mendatang.</p>}
            </div>
          </div>
        )}
        {mode === "checkpoint" && (
          <div className="bg-amber-50 rounded-3xl p-5 border border-amber-100" data-testid="usher-checkpoint-info">
            <p className="font-extrabold text-amber-900 text-sm">Checkpoint Kedua di Lokasi</p>
            <p className="mt-1 text-xs text-amber-700">Pindai kode checkpoint (5 karakter huruf/angka, terpisah dari QR E-Tiket) milik tamu yang sudah lolos scan E-Tiket di pintu masuk, untuk validasi tambahan di lokasi.</p>
          </div>
        )}
      </div>
      <CheckInFlipCard flip={flip} onFinished={() => setFlip(null)} />
    </div>
  );
}
